import React, { useCallback, useMemo, useRef, useState } from 'react';
import { isFinite } from 'lodash';
import { useMount } from 'react-use';
import { Box, Grid, Skeleton, Typography } from '@mui/material';
import { midnightOfDate, trackEvent } from '@surfline/web-common';

import TideChartContainer from 'components/ForecastGraphTide/components/TideChartContainer/TideChartContainer';
import { ForecastGraphsSunlightTable } from 'components/ForecastGraphsSunlightTable/ForecastGraphsSunlightTable';
import { TideReadingsTable } from 'components/TideReadingsTable/TideReadingsTable';
import GraphTooltipProvider from 'containers/ForecastDataVisualizer/GraphTooltips/GraphTooltipProvider';
import type { GraphId } from 'containers/ForecastDataVisualizer/GraphTooltips/GraphTooltipProvider/GraphTooltipProvider';
import TideHeightTooltip from 'containers/ForecastDataVisualizer/GraphTooltips/TideHeightTooltip';
import useTimestamps from 'containers/ForecastDataVisualizer/hooks/useTimestamps';
import useTooltipData from 'containers/ForecastDataVisualizer/hooks/useTooltipData';
import { useMaxWidthTablet } from 'hooks/useMediaQueries';
import { MoveTideLabel, type TideData, type TidePoint } from 'types/tide';
import type { SunlightTimes } from 'types/sunlightTimes';
import { formatTimestampInTimezone } from 'utils/time';

import styles from './TideReportDay.module.scss';

interface Props {
  tide?: TideData;
  sunlightTimes?: SunlightTimes;
  daysToDisplay: Array<number>;
  startTimestamp: number;
  endTimestamp: number;
  dateFormat?: 'MDY' | 'DMY';
  dayIndex?: number;
  spotId: string;
  spotName: string;
}

const TideReportDay: React.FC<Props> = ({
  tide,
  sunlightTimes,
  daysToDisplay,
  startTimestamp,
  endTimestamp,
  dateFormat = 'MDY',
  dayIndex = 0,
  spotId,
  spotName,
}) => {
  const graphContainerRef = useRef<HTMLDivElement | null>(null);
  const [isMounted, setMounted] = useState(false);
  const isMaxWidthTablet = useMaxWidthTablet();

  const flatData = useMemo(
    () =>
      daysToDisplay
        ?.map((day) => tide?.days && tide?.days[day])
        .filter((day): day is TidePoint[] => !!day)
        .flat(),
    [daysToDisplay, tide?.days],
  );

  const utcOffset = useMemo(() => flatData[0]?.utcOffset, [flatData]);

  const startTimestampForGraph = useMemo(
    () => flatData?.[0]?.timestamp || startTimestamp,
    [flatData, startTimestamp],
  );

  const endTimestampForGraph = useMemo(
    () => flatData?.[flatData.length - 1]?.timestamp || endTimestamp,
    [flatData, endTimestamp],
  );

  const date = useMemo(
    () =>
      formatTimestampInTimezone(
        startTimestamp,
        isFinite(utcOffset) ? utcOffset : (tide?.utcOffset ?? 0),
        'EEEE, d MMMM y',
      ),
    [startTimestamp, tide?.utcOffset, utcOffset],
  );

  const midnightOfStartDate = useMemo(
    () => new Date(midnightOfDate(utcOffset, startTimestampForGraph) * 1000),
    [startTimestampForGraph, utcOffset],
  );

  const { initialXPercentage, xScale } = useTimestamps({
    firstDayIndex: 0,
    graphContainerRef,
    isMounted,
    midnightOfStartDate,
    lastDayIndex: 0,
    additionalOffset: false,
  });

  const trackGraphScrubbed = useCallback(
    (graphId: GraphId, additionalSegmentProps: { [key: string]: string | number }) =>
      trackEvent('Graph Scrubbed', {
        forecastEnd: dayIndex + 1,
        forecastStart: dayIndex + 1,
        graphType: graphId,
        moduleName: 'long-range-tide',
        pageName: 'Long Range Tide',
        spotId,
        spotName,
        ...additionalSegmentProps,
      }),
    // Important: the flag used to skip tracking is reset when these props change
    [dayIndex, spotId, spotName],
  );

  const tideDaysToDisplay = useMemo(() => [dayIndex], [dayIndex]);

  const tooltipData = useTooltipData({
    daysToDisplay: tideDaysToDisplay,
    initialXPercentage,
    isGraphFullBleed: false,
    tide: tide as TideData,
    xScale,
  });

  const graphWidth = useMemo(() => {
    const range = xScale?.range();
    return range?.length === 2 ? range[1] - range[0] : 0;
  }, [xScale]);

  useMount(() => {
    setMounted(true);
  });

  return (
    <Box className={styles.container} data-testid="tide-report-day-container">
      <Typography variant="callout1" className={styles.date}>
        {date}
      </Typography>
      {tide && sunlightTimes && flatData ? (
        <Grid container spacing={3} data-testid="tide-report-day-tide-exists">
          {!tide?.error && (
            <Grid item mobile={12} desktopSm={6}>
              {tide?.loading ? (
                <Skeleton
                  variant="rectangular"
                  className={styles.tideChartLoading}
                  data-testid="tide-chart-loading"
                />
              ) : (
                <div className={styles.graphData} ref={graphContainerRef}>
                  {isFinite(tooltipData?.tide?.data?.current?.height) && (
                    <TideHeightTooltip
                      tideHeight={tooltipData?.tide?.data?.current?.height}
                      units={tide.units?.tideHeight}
                      leftPosition={tooltipData?.tide?.leftPosition}
                      graphWidth={graphWidth}
                    />
                  )}
                  <GraphTooltipProvider
                    customStyle={styles.tideGraphVerticalBar}
                    customStyleNow={styles.tideGraphVerticalBarNow}
                    graphId="tide"
                    graphWidth={graphWidth}
                    leftNowPosition={tooltipData?.tide?.leftNowPosition}
                    leftPosition={tooltipData?.tide?.leftPosition}
                    renderTooltip={tooltipData.renderTooltip}
                    timeLabel={tooltipData?.tide?.timeLabel}
                    trackGraphScrubbed={trackGraphScrubbed}
                  >
                    <TideChartContainer
                      tide={tide}
                      sunlightTimes={sunlightTimes}
                      flatData={flatData}
                      startTimestamp={startTimestampForGraph}
                      endTimestamp={endTimestampForGraph}
                      dateFormat={dateFormat}
                      isTableMode
                      dayIndex={dayIndex}
                      showNowPosition={false}
                      moveTideLabel={MoveTideLabel.BOTH}
                      customCssModuleWrapper={styles.tideChartWrapperOverride}
                    />
                  </GraphTooltipProvider>
                </div>
              )}
            </Grid>
          )}
          <Grid desktopSm={tide?.error ? 9 : 3} tablet={tide?.error ? 12 : 6} mobile={12} item>
            <TideReadingsTable
              errorBoundaryClass={styles.tideReadingsError}
              tide={tide?.days?.[dayIndex]}
              units={tide?.units}
              utcOffset={tide?.utcOffset}
              error={tide?.error}
              loading={tide?.loading}
              isTableMode
            />
          </Grid>
          <Grid desktopSm={tide?.error ? 3 : 3} tablet={tide?.error ? 12 : 6} mobile={12} item>
            <Box className={styles.sunlightTable}>
              <ForecastGraphsSunlightTable
                day={dayIndex}
                isColumn={!isMaxWidthTablet}
                sunlightTimes={sunlightTimes}
              />
            </Box>
          </Grid>
        </Grid>
      ) : (
        <Box className={styles.sunlightTable} data-testid="sunlight-table-no-tide">
          <ForecastGraphsSunlightTable
            day={dayIndex}
            isColumn={false}
            sunlightTimes={sunlightTimes}
          />
        </Box>
      )}
    </Box>
  );
};

export default TideReportDay;
